'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import DashboardShell from '@/components/dashboard/DashboardShell';
import { listChains } from '@/lib/chains';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Application error:', error);
  }, [error]);

  return (
    <DashboardShell chain={null} chains={listChains()} entities={[]}>
      <div className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center py-16 text-center sm:py-24">
        <p className="mb-4 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-alert-red">
          Error
        </p>
        <h1 className="mb-4 font-display text-4xl font-black tracking-tight text-parchment sm:text-5xl">
          Something went wrong
        </h1>
        <p className="mb-10 max-w-md font-body text-[15px] leading-relaxed text-parchment-dim">
          This error has been logged. Try again, or pick up the trail from the desk.
        </p>

        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => reset()}
            className="inline-flex items-center justify-center rounded bg-scout-gold px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-ink transition-colors hover:bg-scout-gold/90"
          >
            Try again
          </button>
          <Link
            href="/read"
            className="inline-flex items-center justify-center rounded border-[0.5px] border-divider px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.08em] text-parchment transition-colors hover:border-scout-gold hover:text-scout-gold"
          >
            Read the desk
          </Link>
        </div>

        {/* Error details for development */}
        {process.env.NODE_ENV === 'development' && error.message && (
          <div className="mt-10 max-w-2xl rounded border-[0.5px] border-divider bg-surface px-4 py-3 text-left">
            <p className="break-all font-mono text-[12px] leading-relaxed text-parchment-dim">
              {error.message}
            </p>
            {error.digest && (
              <p className="mt-2 font-mono text-[12px] text-slate-ds">
                Error ID: {error.digest}
              </p>
            )}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
